import React, { useEffect, useState } from 'react';
import { Modal } from '../ui/Modal';
import { Input, Textarea } from '../ui/Input';
import { Button } from '../ui/Button';
import type { VInventoryDashboard } from '../../lib/types';
const UNIT_OPTIONS = [{
  value: 'kg',
  label: 'Kilogramos (kg)'
}, {
  value: 'g',
  label: 'Gramos (g)'
}, {
  value: 'L',
  label: 'Litros (L)'
}, {
  value: 'ml',
  label: 'Mililitros (ml)'
}, {
  value: 'unidades',
  label: 'Unidades'
}, {
  value: 'porciones',
  label: 'Porciones'
}, {
  value: 'latas',
  label: 'Latas'
}, {
  value: 'botellas',
  label: 'Botellas'
}, {
  value: 'paquetes',
  label: 'Paquetes'
}, {
  value: 'cajas',
  label: 'Cajas'
}, {
  value: 'libras',
  label: 'Libras (lb)'
}, {
  value: 'onzas',
  label: 'Onzas (oz)'
}];
interface InventoryItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: VInventoryDashboard | null;
  onSave: (data: any) => Promise<{
    success: boolean;
    error?: string;
  }>;
  onDelete?: () => Promise<{
    success: boolean;
    error?: string;
  }>;
}
export function InventoryItemModal({
  isOpen,
  onClose,
  item,
  onSave,
  onDelete
}: InventoryItemModalProps) {
  const [name, setName] = useState('');
  const [unit, setUnit] = useState('unidades');
  const [costPerUnit, setCostPerUnit] = useState('');
  const [currentStock, setCurrentStock] = useState('');
  const [minimumStock, setMinimumStock] = useState('');
  const [supplier, setSupplier] = useState('');
  const [notes, setNotes] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (isOpen) {
      setName(item?.ingredient_name || '');
      setUnit(item?.unit || 'unidades');
      setCostPerUnit(item?.cost_per_unit?.toString() || '');
      setCurrentStock(item?.current_stock?.toString() || '');
      setMinimumStock(item?.minimum_stock?.toString() || '');
      setSupplier(item?.supplier || '');
      setNotes(item?.notes || '');
      setIsActive(item?.is_active ?? true);
      setError(null);
    }
  }, [isOpen, item]);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      const result = await onSave({
        name,
        unit,
        cost_per_unit: parseFloat(costPerUnit) || 0,
        current_stock: parseFloat(currentStock) || 0,
        minimum_stock: parseFloat(minimumStock) || 0,
        supplier: supplier || null,
        notes: notes || null,
        is_active: isActive
      });
      if (result && !result.success) {
        setError(result.error || 'Error al guardar');
      }
    } catch (err: any) {
      setError(err?.message || 'Error inesperado');
    }
    setIsSubmitting(false);
  };
  return <Modal isOpen={isOpen} onClose={onClose} title={item ? 'Editar Ingrediente' : 'Nuevo Ingrediente'} data-id="element-995" data-mp="35vi4vddf1y74">
      <form onSubmit={handleSubmit} className="space-y-4" data-id="element-996" data-mp="1lc4x5ji2mhso">
        <Input label="Nombre del ingrediente" value={name} onChange={e => setName(e.target.value)} required placeholder="Ej: Queso Mozzarella, Tomate, Aceite" data-id="element-997" data-mp="1qapzppkspdka" />

        <div className="grid grid-cols-2 gap-4" data-id="element-998" data-mp="1zawah3p3zzfr">
          <div data-id="element-999" data-mp="12lx1fmgpatmi">
            <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-1000" data-mp="12mggmveyv3wu">
              Unidad de medida
            </label>
            <select value={unit} onChange={e => setUnit(e.target.value)} className="w-full h-11 px-3 py-2 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-amber" data-id="element-1001" data-mp="16objw5ggjrd9">
              {UNIT_OPTIONS.map(opt => <option key={opt.value} value={opt.value} data-id="element-1002" data-mp="ixnw0aq5cr52">
                  {opt.label}
                </option>)}
            </select>
          </div>
          <Input label="Costo por unidad (₡)" type="number" min="0" step="0.01" value={costPerUnit} onChange={e => setCostPerUnit(e.target.value)} placeholder="0" data-id="element-1003" data-mp="2qiwbsdl150gz" />
        </div>

        <div className="grid grid-cols-2 gap-4" data-id="element-1004" data-mp="2ulj92u0gzdlk">
          <Input label="Stock actual" type="number" min="0" step="0.01" value={currentStock} onChange={e => setCurrentStock(e.target.value)} required placeholder="0" data-id="element-1005" data-mp="2ujq4rldbx5gq" />
          <Input label="Stock mínimo" type="number" min="0" step="0.01" value={minimumStock} onChange={e => setMinimumStock(e.target.value)} required placeholder="0" data-id="element-1006" data-mp="3i9u4hhnecl2g" />
        </div>

        <Input label="Proveedor (opcional)" value={supplier} onChange={e => setSupplier(e.target.value)} placeholder="Ej: Distribuidora Central" data-id="element-1007" data-mp="1jw0xgl6d7txj" />

        <Textarea label="Notas (opcional)" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Notas adicionales sobre este ingrediente" data-id="element-1008" data-mp="1dt5p0xmnhywj" />

        <div className="flex items-center gap-2 pt-2" data-id="element-1009" data-mp="14zkanfeeejnn">
          <input type="checkbox" id="inv-active" checked={isActive} onChange={e => setIsActive(e.target.checked)} className="w-4 h-4 text-brand-amber rounded border-gray-300 focus:ring-brand-amber" data-id="element-1010" data-mp="14y98fk0lpjyn" />
          <label htmlFor="inv-active" className="text-sm font-medium text-gray-700" data-id="element-1011" data-mp="h4hlf6bome6n">
            Ingrediente activo
          </label>
        </div>

        {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3" data-id="element-1012" data-mp="2bhfr1cmfsmnn">
            {error}
          </div>}

        <div className="pt-4 flex justify-between items-center" data-id="element-1013" data-mp="2e1lxps2fqr7w">
          {item && onDelete ? <Button type="button" variant="danger" onClick={onDelete} data-id="element-1014" data-mp="29yzbq7sg40im">
              Eliminar
            </Button> : <div data-id="element-1015" data-mp="2517cesph1c4e" />}
          <div className="flex gap-3" data-id="element-1016" data-mp="252ifeqb3efug">
            <Button type="button" variant="ghost" onClick={onClose} data-id="element-1017" data-mp="2024f5ufqga6z">
              Cancelar
            </Button>
            <Button type="submit" isLoading={isSubmitting} data-id="element-1018" data-mp="2xo1szej63rcl">
              Guardar
            </Button>
          </div>
        </div>
      </form>
    </Modal>;
}